// src/pages/agents/AgentsList.js
import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, CircularProgress, IconButton,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  Snackbar, Alert, Link, Tooltip // <<<--- ADDED Tooltip HERE
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import InfoBox from '../../components/common/InfoBox'; // Assuming you have this component
import { agentService } from '../../api/agentService';
import apiClient from '../../api/axiosConfig'; // Use the configured client

const AgentsList = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();

  const fetchAgents = async () => {
    setLoading(true);
    setError(null); // Clear previous errors
    // Ensure agentService uses apiClient internally or update here
    // const res = await apiClient.get('/api/v1/agents'); // Example if service doesn't use it
    const res = await agentService.listAgents(); // Service should use apiClient now
    if (res.isError) {
      setError(res.message || 'Failed to fetch agents.');
    } else {
      setAgents(Array.isArray(res) ? res : []); // Ensure it's an array
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAgents();
  }, []);

  const handleOpenDeleteDialog = (agent) => {
    setAgentToDelete(agent);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setAgentToDelete(null);
    setDeleteDialogOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (!agentToDelete) return;

    const res = await agentService.deleteAgent(agentToDelete.id);
    handleCloseDeleteDialog(); // Close dialog regardless of outcome

    if (res.isError) {
      setSnackbar({ open: true, message: res.message || 'Failed to delete agent.', severity: 'error' });
    } else {
      setSnackbar({ open: true, message: 'Agent deleted successfully.', severity: 'success' });
      // Refresh the list by filtering out the deleted agent
      setAgents((prev) => prev.filter(a => a.id !== agentToDelete.id));
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Agents</Typography>
        <Button
          variant="contained"
          component={RouterLink}
          to="/agents/new"
          startIcon={<AddIcon />}
        >
          New Agent
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={5}><CircularProgress /></Box>
      ) : error ? (
        // Use InfoBox or MUI Alert for error display
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        // <InfoBox type="error" title="Error">{error}</InfoBox>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Model</TableCell>
                <TableCell>Public</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {agents.length === 0 ? (
                 <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      No agents found. Create one to get started.
                    </TableCell>
                 </TableRow>
              ) : (
                agents.map(agent => (
                  <TableRow key={agent.id} hover>
                    <TableCell>
                      {/* Link the name to the overview page */}
                      <Link component={RouterLink} to={`/agents/${agent.id}/overview`} underline="hover">
                        {agent.name}
                      </Link>
                    </TableCell>
                    <TableCell>{agent.llm_model}</TableCell>
                    <TableCell>{agent.is_public ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{new Date(agent.created_at).toLocaleString()}</TableCell>
                    <TableCell align="right">
                      {/* Keep Edit and Delete, View is implicit via name link */}
                       <Tooltip title="Edit Settings">
                        <IconButton onClick={() => navigate(`/agents/${agent.id}/settings`)} size="small">
                          <EditIcon />
                        </IconButton>
                       </Tooltip>
                       <Tooltip title="Delete Agent">
                        <IconButton onClick={() => handleOpenDeleteDialog(agent)} color="error" size="small">
                          <DeleteIcon />
                        </IconButton>
                       </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete the agent "{agentToDelete?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AgentsList;